import { Stack, Typography } from "@mui/material";
import {
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import TextButton from "@/components/common/buttons/textButton";
import { TEXT_SECONDARY } from "@/config/mui/colorPalette";

export type ConfirmationCheckboxesProps<T> = {
  /** Array of field names (as react-hook-form paths) for the checkboxes */
  checkboxNames: Path<T>[];
  /** Optional labels for the toggle button */
  selectAllLabel?: { select: string; deselect: string };
  /** The checkboxes (or other components) to render below the header */
  children: React.ReactNode;
  /** getValues from react-hook-form */
  getValues: UseFormGetValues<T>;
  /** setValue from react-hook-form */
  setValue: UseFormSetValue<T>;
};

export function ConfirmationCheckboxes<T>({
  checkboxNames,
  selectAllLabel,
  children,
  getValues,
  setValue,
}: ConfirmationCheckboxesProps<T>) {
  // Compute whether all checkboxes are checked.
  const allAreChecked = checkboxNames.every((name) => getValues(name) === true);

  const handleToggleAll = () => {
    const allCheckedNow = checkboxNames.every(
      (name) => getValues(name) === true
    );
    checkboxNames.forEach((name) => {
      // Cast the boolean to satisfy TypeScript.
      setValue(name, !allCheckedNow as unknown as PathValue<T, Path<T>>);
    });
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: "100%", mt: 1 }}
      >
        <Typography variant="subtitleSmall" sx={{ color: TEXT_SECONDARY }}>
          Confirmations
        </Typography>
        {checkboxNames.length >= 2 && (
          <TextButton onClick={handleToggleAll}>
            {allAreChecked
              ? selectAllLabel?.deselect || "Deselect all"
              : selectAllLabel?.select || "Select all"}
          </TextButton>
        )}
      </Stack>
      {children}
    </>
  );
}
